import { Component, OnInit, signal, inject, OnDestroy, ViewChild, ElementRef, AfterViewInit, computed, PLATFORM_ID, NgZone } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Events, PhotoEntry, AppEvent } from '../event';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { exitPresentation, requestPresentationFullscreen } from '../presentation';

interface PositionedPhoto extends PhotoEntry {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  rotation: number;
  targetRotation: number;
  baseRotation: number;
  scale: number;
  targetScale: number;
  originalScale: number;
  zIndex: number;
}

@Component({
  selector: 'app-gallery',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    <div #container class="min-h-screen overflow-hidden flex flex-col font-sans relative select-none bg-slate-900 bg-cover bg-center" 
         [style.fontFamily]="event()?.font || 'Inter'" 
         [style.backgroundImage]="isVideo(event()?.galleryBackgroundUrl) ? 'none' : backgroundStyle()"
         (mousemove)="onMouseMove()"
         (touchstart)="onMouseMove()">
      
      @if (event()?.galleryBackgroundUrl && isVideo(event()?.galleryBackgroundUrl)) {
         <video #backgroundVideo [src]="event()?.galleryBackgroundUrl" class="absolute inset-0 z-0 h-full w-full object-cover pointer-events-none transform-gpu" autoplay [muted]="backgroundVideoShouldBeMuted()" loop playsinline preload="auto" disablepictureinpicture (pause)="onBackgroundVideoPause()"></video>
         <div class="absolute inset-0 z-0 bg-black/40 pointer-events-none"></div>
      }

      @if (showBackgroundAudioButton()) {
        <button type="button" (click)="unlockBackgroundAudio()" class="fixed bottom-8 left-8 z-[1100] flex items-center gap-2 rounded-full border border-white/15 bg-black/45 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-xl backdrop-blur-md active:scale-95 pointer-events-auto">
          <mat-icon class="text-[18px]">volume_up</mat-icon>
          <span>Activar sonido</span>
        </button>
      }

      <div class="relative z-10 w-full flex-1 flex flex-col h-full pointer-events-none">
         <!-- Event Title -->
         @if (event()?.showGalleryTitle !== false && event()?.galleryLayoutMode !== 'center-title') {
        <div class="absolute top-0 left-0 w-full h-[15vh] flex items-center justify-center pointer-events-none z-20 overflow-visible">
            <div class="flex flex-col items-center gap-2 text-center"
                 [style.transform]="'translate(' + (event()?.logoPosition?.left || 0) + 'vw, ' + (event()?.logoPosition?.top || 0) + 'vh) scale(' + (event()?.logoScale || 1) + ')'"
                 [style.transformOrigin]="'top center'">
           @if (event()?.titleImageUrl) {
              <img [src]="event()!.titleImageUrl" class="h-20 md:h-28 object-contain filter drop-shadow-[0_10px_20px_rgba(0,0,0,0.5)] transition-all duration-500 hover:scale-105" alt="Title" loading="eager" />
           } @else {
              <div class="px-8 py-3 bg-black/40 backdrop-blur-xl border border-white/10 rounded-full shadow-2xl">
                <h1 class="text-2xl md:text-5xl font-bold tracking-tight text-white uppercase">
                   {{ event()?.title }}
                </h1>
              </div>
           }
           @if (event()?.additionalImageUrl) {
              <img [src]="event()!.additionalImageUrl" class="h-10 md:h-14 object-contain filter drop-shadow-[0_5px_15px_rgba(0,0,0,0.5)]" alt="Additional Logo" loading="eager" />
           }
            </div>
        </div>
      }

      
      @if (event()?.galleryLayoutMode === 'center-title' && event()?.showGalleryTitle !== false) {
        <div class="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
           <div class="bg-white/95 backdrop-blur shadow-2xl border border-slate-200 px-12 py-8 rounded-3xl" 
                [style.transform]="'translate(' + (event()?.logoPosition?.left || 0) + 'vw, ' + (event()?.logoPosition?.top || 0) + 'vh) scale(' + (event()?.logoScale || 1) + ') rotate(-2deg)'"
                style="display: flex; align-items: center; justify-content: center; flex-direction: column; width: 45vw; height: 35vh;">
             @if (event()?.titleImageUrl) {
                <img [src]="event()!.titleImageUrl" class="max-h-[18vh] object-contain mb-4 mx-auto" alt="Title" />
             } @else {
                <h1 class="text-6xl md:text-9xl font-bold tracking-tight drop-shadow-sm text-slate-900 text-center">{{ event()?.title }}</h1>
             }
             @if (event()?.additionalImageUrl) {
                <img [src]="event()!.additionalImageUrl" class="max-h-[10vh] object-contain mt-2 mx-auto" alt="Additional Text" />
             } @else {
                <p class="text-2xl md:text-4xl text-slate-500 mt-2 font-serif italic text-center">Festejemos Juntos</p>
             }
           </div>
        </div>
      }

      <main class="flex-1 relative w-full h-full overflow-hidden">
         @if (photos().length === 0) {
            <div class="absolute inset-0 flex flex-col items-center justify-center">
               <div class="w-32 h-32 bg-white/5 backdrop-blur rounded-full flex items-center justify-center mb-6 text-white/40 border border-white/10">
                  <mat-icon class="text-6xl">photo_camera</mat-icon>
               </div>
               <h3 class="text-3xl font-bold text-white mb-2 tracking-tight drop-shadow-md">El mural está vacío</h3>
            </div>
         } @else {
            <!-- MURAL CONTAINER CLIPPED TO ADMIN AREA -->
            <div class="absolute overflow-hidden"
                 [style.top.vh]="galleryClipTop()"
                 [style.left.vw]="galleryClipLeft()"
                 [style.width.vw]="galleryClipWidth()"
                 [style.height.vh]="galleryClipHeight()">
               
               <!-- INNER WRAPPER TO PRESERVE SCREEN-RELATIVE COORDS -->
               <div class="absolute" 
                    [style.left.vw]="-galleryClipLeft()" 
                    [style.top.vh]="-galleryClipTop()" 
                    style="width: 100vw; height: 100vh;">
                  
                  @for (photo of positionedPhotos(); track photo.id) {
                     <div class="photo-element absolute cursor-pointer will-change-transform pointer-events-auto transition-[border,box-shadow]"
                          [attr.data-id]="photo.id"
                          [style.left.%]="photo.x"
                          [style.top.%]="photo.y"
                          [style.zIndex]="photo.zIndex"
                          [style.transform]="'translate(-50%, -50%) perspective(2000px) rotate(' + photo.rotation + 'deg) rotateX(' + ((photo.y - 50) * 0.2) + 'deg) rotateY(' + ((photo.x - 50) * -0.2) + 'deg) scale(' + photo.scale + ')'"
                          [style.boxShadow]="hoveredId() === photo.id ? '0 40px 80px -15px rgba(0,0,0,0.6)' : '0 20px 40px -12px rgba(0,0,0,0.4)'"
                          (mouseenter)="hoverPhoto(photo)"
                          (mouseleave)="unhoverPhoto(photo)">
                        
                        <div class="bg-white p-3 pt-4 md:p-4 md:pt-5 pb-4 md:pb-5 rounded-sm border-2 border-slate-200/60 shadow-inner overflow-hidden relative flex flex-col" [style.width.px]="getResponsiveWidth()">
                           <div class="relative w-full overflow-hidden bg-slate-100 shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]" [style.aspectRatio]="photoRatio(photo)">
                             <img [src]="photo.imageUrl" class="absolute inset-0 w-full h-full object-cover pointer-events-none" alt="Photo" loading="lazy" />
                             @if (event()?.frameUrl) {
                               <div class="absolute z-10 pointer-events-none"
                                    [style.left.%]="event()?.frameArea?.left || 0"
                                    [style.top.%]="event()?.frameArea?.top || 0"
                                    [style.width.%]="event()?.frameArea?.width || 100"
                                    [style.height.%]="event()?.frameArea?.height || 100">
                                  <img [src]="event()?.frameUrl" class="w-full h-full object-fill pointer-events-none" alt="Frame" loading="lazy" />
                                </div>
                             }
                           </div>
                           <div class="mt-3 md:mt-4 text-center px-1 flex flex-col justify-center">
                              @if (photo.message) {
                                <p class="text-slate-800 font-semibold text-lg md:text-xl whitespace-normal break-words leading-tight font-handwriting" style="font-family: 'Caveat', cursive; overflow-wrap: anywhere;">{{ photo.message }}</p>
                              } @else {
                                <p class="text-slate-500 font-medium text-xs md:text-sm whitespace-normal break-words uppercase tracking-widest" style="overflow-wrap: anywhere;">{{ photo.guestName }}</p>
                              }
                           </div>
                        </div>
                     </div>
                  }
               </div>

               <!-- Gradient Fades at edges -->
               <div class="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-slate-900/60 via-slate-900/20 to-transparent pointer-events-none z-50"></div>
               <div class="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-slate-900/60 via-slate-900/20 to-transparent pointer-events-none z-50"></div>
            </div>
         }
      </main>
      
      <!-- Latest Photo Pop-up -->
      @if (latestPhoto()) {
         <div class="fixed inset-0 flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-md transition-opacity duration-500 pointer-events-none" style="z-index: 2147483000;">
            <div class="bg-white p-6 rounded-sm shadow-[0_30px_60px_rgba(0,0,0,0.6)] max-w-2xl w-full relative transform scale-100 rotate-[-1deg]">
               <div class="w-full relative overflow-hidden bg-slate-100 mb-6 border border-slate-100" [style.aspectRatio]="photoRatio(latestPhoto()) || '3/4'">
                  <img [src]="latestPhoto()?.imageUrl" class="absolute inset-0 w-full h-full object-cover" alt="Latest photo" />
                  @if (event()?.frameUrl) {
                    <div class="absolute z-10 pointer-events-none"
                         [style.left.%]="event()?.frameArea?.left || 0"
                         [style.top.%]="event()?.frameArea?.top || 0"
                         [style.width.%]="event()?.frameArea?.width || 100"
                         [style.height.%]="event()?.frameArea?.height || 100">
                      <img [src]="event()?.frameUrl" class="w-full h-full object-fill pointer-events-none" alt="Frame" />
                    </div>
                  }
               </div>
               <div class="text-center space-y-2">
                 <p class="text-4xl text-slate-800 font-bold whitespace-normal break-words leading-tight" style="font-family: 'Caveat', cursive; overflow-wrap: anywhere;">{{ latestPhoto()?.message || latestPhoto()?.guestName }}</p>
                 @if (latestPhoto()?.message) {
                    <p class="text-sm uppercase font-bold text-slate-400 tracking-widest mt-2 break-words" style="overflow-wrap: anywhere;">{{ latestPhoto()?.guestName }}</p>
                 }
               </div>
            </div>
         </div>
      }

      <!-- Screen Controls (Visible on hover) -->
      <div class="fixed bottom-8 right-8 z-[1100] transition-opacity duration-300 flex flex-col gap-3 items-end pointer-events-auto" [ngClass]="{'opacity-100': showControls(), 'opacity-0': !showControls()}">
        <div class="px-4 py-2 bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl flex items-center gap-3 shadow-2xl transition-all duration-500"
             [ngClass]="isSocketConnected() ? 'border-emerald-500/30' : 'border-red-500/30'">
           <div class="relative flex h-2 w-2">
              @if (isSocketConnected()) {
                 <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                 <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]"></span>
              } @else {
                 <span class="animate-pulse absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                 <span class="relative inline-flex rounded-full h-2 w-2 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]"></span>
              }
           </div>
           <span class="text-[10px] font-black italic uppercase tracking-tighter transition-colors duration-500"
                 [ngClass]="isSocketConnected() ? 'text-emerald-400' : 'text-red-400'">
             {{ isSocketConnected() ? 'Sincronizado' : 'Conectando mural...' }}
           </span>
        </div>
        <button (click)="handlePresentationControl()" class="w-14 h-14 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-white/20 transition-all">
           <mat-icon>{{ isFullscreen() ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
        </button>
      </div>
    </div>
  `
})
export class Gallery implements OnInit, OnDestroy, AfterViewInit {
  private baseEventSvc = inject(Events);
  private route = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);
  private platformId = inject(PLATFORM_ID);
  private zone = inject(NgZone);

  event = signal<AppEvent | null>(null);
  photos = signal<PhotoEntry[]>([]);
  positionedPhotos = signal<PositionedPhoto[]>([]);
  latestPhoto = signal<PhotoEntry | null>(null);
  
  showControls = signal(true);
  isFullscreen = signal(false);
  isSocketConnected = signal(false);
  presentationMode = signal(false);
  backgroundAudioUnlocked = signal(false);
  showBackgroundAudioButton = signal(false);
  private hideControlsTimeout: ReturnType<typeof setTimeout> | undefined;
  
  private popupTimeout: ReturnType<typeof setTimeout> | undefined;
  private latestPhotoQueue: PhotoEntry[] = [];
  private readonly latestPhotoDisplayMs = 6000;
  private rotationTimer: ReturnType<typeof setInterval> | undefined;
  private gallerySyncTimer: ReturnType<typeof setInterval> | undefined;
  private animFrame: number | null = null;
  private lastPhysicsAt = 0;
  private currentEventId = '';
  private reconnectRefreshPending = false;
  private gallerySyncPending = false;
  private rotationCursor = 0;
  private readonly maxVisiblePhotoLimit = 50;
  private readonly galleryBatchSize = 120;
  private readonly flowRotationBatchSize = 8;
  private readonly maxPreloadedImages = 90;
  private flowReplacementQueue: PhotoEntry[] = [];
  private recentPhotoPriorityIds = new Map<string, number>();
  private galleryPhotosOffset = 0;
  private galleryHasMorePhotos = true;
  private galleryBatchLoading = false;
  private preloadedImages = new Map<string, HTMLImageElement>();
  private highestZIndex = 10;
  hoveredId = signal<string | null>(null);
  private fsHandler: any;
  
  shapeTextPoints = signal<{x: number, y: number}[]>([]);
  
  safeYoutubeUrl = computed(() => {
     const videoId = this.event()?.galleryYoutubeVideoId;
     if (!videoId) return null;
     const origin = typeof window !== 'undefined' ? window.location.origin : '';
     // Added disablekb=1, fs=0, playsinline=1, autohide=1 to aggressively hide controls
     return this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&enablejsapi=1&disablekb=1&fs=0&playsinline=1&autohide=1&origin=${encodeURIComponent(origin)}`);
  });
  
  hoverPhoto(photo: PositionedPhoto) {
     this.hoveredId.set(photo.id);
     this.highestZIndex++;
     photo.zIndex = this.highestZIndex;
  }
  
  unhoverPhoto(photo: PositionedPhoto) {
     if (this.hoveredId() === photo.id) {
        this.hoveredId.set(null);
     }
  }

  @ViewChild('container') container!: ElementRef;
  @ViewChild('backgroundVideo') backgroundVideoRef?: ElementRef<HTMLVideoElement>;

  ngOnInit() {
    this.presentationMode.set(this.route.snapshot.queryParamMap.get('presentation') === '1');
    this.route.params.subscribe(async params => {
      const eventIdentifier = params['id'];
      const evt = await firstValueFrom(this.baseEventSvc.getEvent(eventIdentifier));
      const eventId = evt.id;
      this.currentEventId = eventId;
      
      this.baseEventSvc.joinEvent(eventId);

      this.baseEventSvc.connectionStatus$.subscribe(status => {
         this.isSocketConnected.set(status);
         if (status) this.refreshAfterReconnect();
      });

      this.event.set(evt);
      setTimeout(() => this.syncBackgroundVideoSettings(), 0);
      if (evt.galleryLayoutMode === 'shape' && evt.galleryShapeText) {
         this.shapeTextPoints.set(this.computeShapePoints(evt.galleryShapeText));
      }

      const initialPhotos = await this.loadGalleryPhotoBatch(true);
      const limitedPhotos = initialPhotos.slice(0, this.getVisiblePhotoLimit());
      limitedPhotos.forEach(p => this.preloadImage(p));
      
      const positioned = limitedPhotos.map(p => this.createPositionedPhoto(p));
      this.positionedPhotos.set(positioned);
      this.recalculateLayout();

      this.baseEventSvc.newPhoto$.subscribe((photo: PhotoEntry) => {
         if (photo.eventId !== eventId) return;
         this.photos.update(list => [photo, ...list]);
         this.preloadImage(photo);
         
         const newPos = this.createPositionedPhoto(photo);
         this.prioritizeIncomingPhoto(newPos);
         
         this.positionedPhotos.update(list => {
            const mode = this.event()?.galleryLayoutMode || 'infinite-wall';
            const limit = this.getVisiblePhotoLimit();
            const limited = list.slice(0, limit - 1);
            if (mode === 'carousel' || mode === 'infinite-wall') {
              this.placePhotoAtFlowEnd(newPos, limited);
              return [...limited, newPos];
            }
            return [newPos, ...limited];
         });
         
         setTimeout(() => {
           this.recalculateLayout();
         }, 100);

         this.showLatestPopup(photo);
      });
      
      this.baseEventSvc.photoRemoved$.subscribe((photoId: string) => {
         this.photos.update(list => list.filter(p => p.id !== photoId));
         this.positionedPhotos.update(list => list.filter(p => p.id !== photoId));
         if (this.latestPhoto()?.id === photoId) {
            this.latestPhoto.set(null);
         }
         this.recalculateLayout();
      });

      this.baseEventSvc.eventUpdated$.subscribe((updatedEvent: AppEvent) => {
         if (updatedEvent.id !== eventId) return;
         this.event.set({...this.event(), ...updatedEvent} as AppEvent);
         setTimeout(() => this.syncBackgroundVideoSettings(), 0);
         
         if (updatedEvent.galleryLayoutMode === 'shape' && updatedEvent.galleryShapeText) {
           this.shapeTextPoints.set(this.computeShapePoints(updatedEvent.galleryShapeText));
         }
         
         this.recalculateLayout();
      });

      this.startPhotoRotation();
      this.startGallerySync();
    });
    
    this.onMouseMove(); // Initialize controls fade out
    this.setupFullscreenListener();
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.startPhysicsLoop();
      if (this.presentationMode()) {
        setTimeout(() => requestPresentationFullscreen(this.container.nativeElement), 250);
      }
    }
  }

  private async refreshAfterReconnect() {
    if (!this.currentEventId || this.reconnectRefreshPending) return;
    this.reconnectRefreshPending = true;
    try {
      this.baseEventSvc.joinEvent(this.currentEventId);
      const [event, photos] = await Promise.all([
        firstValueFrom(this.baseEventSvc.getEvent(this.currentEventId)),
        firstValueFrom(this.baseEventSvc.getEventPhotos(this.currentEventId, false, { limit: this.galleryBatchSize, offset: 0 }))
      ]);
      this.event.set(event);
      setTimeout(() => this.syncBackgroundVideoSettings(), 0);
      const approvedPhotos = photos;
      const currentIds = new Set(this.photos().map(photo => photo.id));
      const remoteIds = new Set(approvedPhotos.map(photo => photo.id));
      const needsPhotoSync = approvedPhotos.some(photo => !currentIds.has(photo.id));
      if (needsPhotoSync) {
        this.photos.set(this.mergePhotos(approvedPhotos, this.photos()).slice(0, Math.max(this.photos().length, this.galleryBatchSize)));
        this.galleryPhotosOffset = Math.max(this.galleryPhotosOffset, this.photos().length);
        const limit = this.getVisiblePhotoLimit();
        this.photos().slice(0, limit).forEach(photo => this.preloadImage(photo));
        const existingById = new Map(this.positionedPhotos().map(photo => [photo.id, photo]));
        this.positionedPhotos.set(this.photos().slice(0, limit).map(photo => existingById.get(photo.id) || this.createPositionedPhoto(photo)));
        this.trimPreloadedImages();
        this.recalculateLayout();
      } else if (this.positionedPhotos().some(photo => !remoteIds.has(photo.id))) {
        this.positionedPhotos.update(list => list.filter(photo => remoteIds.has(photo.id)));
        this.trimPreloadedImages();
      }
    } finally {
      this.reconnectRefreshPending = false;
    }
  }

  private startGallerySync() {
    if (this.gallerySyncTimer) clearInterval(this.gallerySyncTimer);
    if (!isPlatformBrowser(this.platformId)) return;
    this.gallerySyncTimer = setInterval(() => {
      void this.syncLatestGalleryPhotos();
    }, 30000);
  }

  private async syncLatestGalleryPhotos() {
    if (!this.currentEventId || this.gallerySyncPending) return;
    this.gallerySyncPending = true;
    try {
      const photos = await firstValueFrom(this.baseEventSvc.getEventPhotos(this.currentEventId, false, {
        limit: this.galleryBatchSize,
        offset: 0
      }));
      const beforeIds = new Set(this.photos().map(photo => photo.id));
      const merged = this.mergePhotos(photos, this.photos());
      const hasNewPhotos = photos.some(photo => !beforeIds.has(photo.id));
      this.photos.set(merged);
      this.galleryPhotosOffset = Math.max(this.galleryPhotosOffset, photos.length);
      this.galleryHasMorePhotos = photos.length === this.galleryBatchSize;

      if (hasNewPhotos && this.positionedPhotos().length < Math.min(this.getVisiblePhotoLimit(), merged.length)) {
        const existingById = new Map(this.positionedPhotos().map(photo => [photo.id, photo]));
        const nextVisible = merged.slice(0, this.getVisiblePhotoLimit());
        nextVisible.forEach(photo => this.preloadImage(photo));
        this.positionedPhotos.set(nextVisible.map(photo => existingById.get(photo.id) || this.createPositionedPhoto(photo)));
        this.recalculateLayout();
      }

      this.trimPreloadedImages();
    } catch (error) {
      console.error('Failed to sync gallery photos', error);
    } finally {
      this.gallerySyncPending = false;
    }
  }

  private async loadGalleryPhotoBatch(reset = false): Promise<PhotoEntry[]> {
    if (!this.currentEventId || this.galleryBatchLoading) return [];
    this.galleryBatchLoading = true;
    try {
      const offset = reset ? 0 : this.galleryPhotosOffset;
      const photos = await firstValueFrom(this.baseEventSvc.getEventPhotos(this.currentEventId, false, {
        limit: this.galleryBatchSize,
        offset
      }));
      if (reset) {
        this.photos.set(photos);
        this.galleryPhotosOffset = photos.length;
      } else if (photos.length) {
        this.photos.set(this.mergePhotos(this.photos(), photos));
        this.galleryPhotosOffset += photos.length;
      }
      this.galleryHasMorePhotos = photos.length === this.galleryBatchSize;
      return photos;
    } catch (error) {
      console.error('Failed to load gallery photo batch', error);
      return [];
    } finally {
      this.galleryBatchLoading = false;
    }
  }

  private mergePhotos(primary: PhotoEntry[], secondary: PhotoEntry[]) {
    const seen = new Set<string>();
    const merged: PhotoEntry[] = [];
    for (const photo of [...primary, ...secondary]) {
      if (seen.has(photo.id)) continue;
      seen.add(photo.id);
      merged.push(photo);
    }
    return merged;
  }

  private getVisiblePhotoLimit() {
    const mode = this.event()?.galleryLayoutMode || 'infinite-wall';
    if (mode === 'grid') {
      return this.getGridMetrics().capacity;
    }
    if (mode === 'floating') {
      return this.getFloatingMetrics().capacity;
    }
    if (mode === 'carousel' || mode === 'infinite-wall') {
      return this.getFlowVisibleLimit();
    }
    return 32;
  }

  private getAreaMetrics(area = this.event()?.galleryArea || { top: 15, left: 5, width: 90, height: 80 }) {
    const W = typeof window !== 'undefined' ? window.innerWidth : 1000;
    const H = typeof window !== 'undefined' ? window.innerHeight : 800;
    const areaWpx = Math.max(120, (area.width / 100) * W);
    const areaHpx = Math.max(120, (area.height / 100) * H);
    return {
      W,
      H,
      area,
      areaWpx,
      areaHpx,
      ratio: areaWpx / areaHpx
    };
  }

  private getFlowVisibleLimit() {
    const { areaWpx, ratio } = this.getAreaMetrics();
    if (ratio >= 1.35 && areaWpx >= 900) return 36;
    if (ratio >= 1.0) return 28;
    if (ratio >= 0.7) return 22;
    return 16;
  }

  private getGridMetrics(count?: number) {
    const { W, areaWpx, areaHpx } = this.getAreaMetrics();
    const minCellW = W > 1200 ? 185 : W > 768 ? 160 : 130;
    const minCellH = W > 1200 ? 230 : W > 768 ? 210 : 185;
    let maxCols = Math.max(1, Math.floor(areaWpx / minCellW));
    let maxRows = Math.max(1, Math.floor(areaHpx / minCellH));
    while (maxCols * maxRows > this.maxVisiblePhotoLimit) {
      if (maxCols >= maxRows && maxCols > 1) {
        maxCols--;
      } else if (maxRows > 1) {
        maxRows--;
      } else {
        break;
      }
    }
    const capacity = Math.max(1, maxCols * maxRows);

    if (!count || count >= capacity) {
      return { rows: maxRows, cols: maxCols, capacity };
    }

    const areaRatio = areaWpx / areaHpx;
    const cols = Math.max(1, Math.min(maxCols, Math.ceil(Math.sqrt(count * areaRatio))));
    const rows = Math.max(1, Math.min(maxRows, Math.ceil(count / cols)));
    return { rows, cols, capacity: Math.max(count, rows * cols) };
  }

  private getFloatingMetrics() {
    const { areaWpx, areaHpx, ratio } = this.getAreaMetrics();
    const cols = ratio < 0.75
      ? (areaWpx > 520 ? 3 : 2)
      : areaWpx > 1700 ? 9 : areaWpx > 1450 ? 8 : areaWpx > 1180 ? 7 : areaWpx > 920 ? 6 : areaWpx > 620 ? 4 : 3;
    const rows = areaHpx > 820 ? 4 : areaHpx > 560 ? 3 : 2;
    return { cols, rows, capacity: Math.min(36, cols * rows) };
  }

  computeShapePoints(text: string): {x: number, y: number}[] {
    if (!text || typeof document === 'undefined') return [];
    
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    if (!ctx) return [];
    
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, 800, 600);
    
    ctx.fillStyle = 'black';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    let fontSize = 400;
    ctx.font = `bold ${fontSize}px sans-serif`;
    while (ctx.measureText(text).width > 700 && fontSize > 20) {
      fontSize -= 20;
      ctx.font = `bold ${fontSize}px sans-serif`;
    }
    
    ctx.fillText(text, 400, 300);
    
    const imageData = ctx.getImageData(0, 0, 800, 600).data;
    const points = [];
    
    const stepX = 40;
    const stepY = 40;
    
    for (let y = 0; y < 600; y += stepY) {
      for (let x = 0; x < 800; x += stepX) {
        const i = (y * 800 + x) * 4;
        const r = imageData[i];
        if (r < 128) {
           const px = (x / 800) * 80 + 10;
           const py = (y / 600) * 80 + 10;
           points.push({ x: px, y: py });
        }
      }
    }
    return points.sort(() => Math.random() - 0.5);
  }

  backgroundStyle() {
    const bgUrl = this.event()?.galleryBackgroundUrl;
    if (bgUrl) {
      return `url(${bgUrl})`;
    }
    return `radial-gradient(#1e293b 1.5px, transparent 1.5px)`;
  }

  galleryClipLeft() {
    const area = this.event()?.galleryArea || { top: 15, left: 5, width: 90, height: 80 };
    return Math.max(0, area.left - this.galleryHorizontalBleed());
  }

  galleryClipTop() {
    const area = this.event()?.galleryArea || { top: 15, left: 5, width: 90, height: 80 };
    return Math.max(0, area.top);
  }

  galleryClipWidth() {
    const area = this.event()?.galleryArea || { top: 15, left: 5, width: 90, height: 80 };
    const left = this.galleryClipLeft();
    const right = Math.min(100, area.left + area.width + this.galleryHorizontalBleed());
    return Math.max(1, right - left);
  }

  galleryClipHeight() {
    const area = this.event()?.galleryArea || { top: 15, left: 5, width: 90, height: 80 };
    const top = this.galleryClipTop();
    const bottom = Math.min(100, area.top + area.height);
    return Math.max(1, bottom - top);
  }

  private galleryHorizontalBleed() {
    return 0;
  }

  onMouseMove() {
    this.showControls.set(true);
    if (this.hideControlsTimeout) clearTimeout(this.hideControlsTimeout);
    this.hideControlsTimeout = setTimeout(() => {
      this.showControls.set(false);
    }, 3000);
  }

  isVideo(url: string | undefined | null): boolean {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
  }

  private syncBackgroundVideoSettings() {
    if (!isPlatformBrowser(this.platformId)) return;
    const video = this.backgroundVideoRef?.nativeElement;
    const currentEvent = this.event();
    if (!video || !currentEvent?.galleryBackgroundUrl || !this.isVideo(currentEvent.galleryBackgroundUrl)) return;

    if (currentEvent.galleryBackgroundVideoMuted !== false) {
      this.backgroundAudioUnlocked.set(false);
      this.showBackgroundAudioButton.set(false);
    }
    const shouldMute = this.backgroundVideoShouldBeMuted();
    video.defaultMuted = shouldMute;
    video.muted = shouldMute;
    if (shouldMute) {
      video.setAttribute('muted', '');
    } else {
      video.removeAttribute('muted');
    }
    video.volume = shouldMute ? 0 : 1;
    this.showBackgroundAudioButton.set(currentEvent.galleryBackgroundVideoMuted === false && shouldMute);

    video.play().catch(() => this.fallbackToMutedPlayback());
  }

  onBackgroundVideoPause() {
    if (!isPlatformBrowser(this.platformId)) return;
    window.setTimeout(() => {
      const video = this.backgroundVideoRef?.nativeElement;
      if (!video || !video.paused) return;
      video.play().catch(() => this.fallbackToMutedPlayback());
    }, 120);
  }

  backgroundVideoShouldBeMuted(): boolean {
    const currentEvent = this.event();
    if (currentEvent?.galleryBackgroundVideoMuted !== false) return true;
    return this.requiresGestureForBackgroundAudio() && !this.backgroundAudioUnlocked();
  }

  async unlockBackgroundAudio() {
    const video = this.backgroundVideoRef?.nativeElement;
    if (!video) return;
    this.backgroundAudioUnlocked.set(true);
    this.showBackgroundAudioButton.set(false);
    video.muted = false;
    video.volume = 1;
    try {
      await video.play();
    } catch {
      this.backgroundAudioUnlocked.set(false);
      this.fallbackToMutedPlayback();
    }
  }

  private fallbackToMutedPlayback() {
    const video = this.backgroundVideoRef?.nativeElement;
    if (!video) return;
    video.muted = true;
    video.volume = 0;
    this.showBackgroundAudioButton.set(this.event()?.galleryBackgroundVideoMuted === false);
    video.play().catch(() => {
      // If muted playback is also blocked, the browser is preventing media until interaction.
    });
  }

  private requiresGestureForBackgroundAudio(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    const ua = navigator.userAgent || '';
    return /Android|iPhone|iPad|iPod/i.test(ua) || window.matchMedia?.('(hover: none), (pointer: coarse)').matches === true;
  }

  toggleFullscreen() {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const elem = this.container.nativeElement;
    
    if (!document.fullscreenElement && 
        !(document as any).webkitFullscreenElement && 
        !(document as any).mozFullScreenElement && 
        !(document as any).msFullscreenElement) {
      
      const requestMethod = elem.requestFullscreen || 
                          elem.webkitRequestFullscreen || 
                          (elem as any).mozRequestFullScreen || 
                          (elem as any).msRequestFullscreen;

      if (requestMethod) {
        try {
          const result = requestMethod.call(elem);
          if (result instanceof Promise) {
            result.catch((err: any) => {
              console.error(`Error attempting to enable full-screen mode: ${err.message}`);
              window.open(window.location.href, '_blank');
            });
          }
        } catch (err) {
          window.open(window.location.href, '_blank');
        }
      } else {
        window.open(window.location.href, '_blank');
      }
    } else {
      const exitMethod = document.exitFullscreen || 
                       (document as any).webkitExitFullscreen || 
                       (document as any).mozCancelFullScreen || 
                       (document as any).msExitFullscreen;
      
      if (exitMethod) {
        try {
          exitMethod.call(document);
        } catch (err) {
           console.error('Error exiting fullscreen', err);
        }
      }
    }
  }

  handlePresentationControl() {
    if (!this.presentationMode()) {
      this.toggleFullscreen();
      return;
    }

    if (this.isFullscreen()) {
      exitPresentation();
      return;
    }

    this.toggleFullscreen();
  }

  private setupFullscreenListener() {
    if (!isPlatformBrowser(this.platformId)) return;
    
    this.fsHandler = () => {
      this.isFullscreen.set(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', this.fsHandler);
    document.addEventListener('webkitfullscreenchange', this.fsHandler);
    document.addEventListener('mozfullscreenchange', this.fsHandler);
    document.addEventListener('MSFullscreenChange', this.fsHandler);
  }

  recalculateLayout() {
     const photos = this.positionedPhotos();
     if (photos.length === 0) return;
     
     const W = typeof window !== 'undefined' ? window.innerWidth : 1000;
     const H = typeof window !== 'undefined' ? window.innerHeight : 800;
     const basePhotoW = this.getResponsiveWidth();

     const mode = this.event()?.galleryLayoutMode || 'infinite-wall';
     const area = this.event()?.galleryArea || { top: 15, left: 5, width: 90, height: 80 };
     const areaMetrics = this.getAreaMetrics(area);
     
     if (mode === 'carousel' || mode === 'infinite-wall') {
        const count = photos.length;
        const isMobile = W < 768;
        const flowSpacing = this.getFlowSpacing(count, area);
        const flowStartX = area.left + Math.min(flowSpacing * 0.5, area.width * 0.08);
        const flowY = this.getFlowCenterY(area);
        photos.forEach((p, i) => {
           p.baseRotation = (Math.random() - 0.5) * 8;
           p.targetRotation = p.baseRotation;
           
           p.targetX = flowStartX + (i * flowSpacing);
           p.targetY = flowY;
           
           const maxScaleH = (area.height / 100 * H) / (basePhotoW * 1.5);
           const flowSpacingPx = (flowSpacing / 100) * W;
           const maxScaleW = flowSpacingPx / (basePhotoW * 0.95);
           const mobileScaleLimit = isMobile ? 0.7 : 1.8;
           const areaAwareScale = areaMetrics.ratio >= 1.35 && area.width >= 70
             ? maxScaleH * 0.7
             : Math.min(maxScaleH * 0.82, maxScaleW * 1.05);
           p.targetScale = Math.min(mobileScaleLimit, areaAwareScale);
           p.originalScale = p.targetScale;
           
           if (p.x === 50 && p.y === 50) {
              p.x = p.targetX + 50; 
              p.y = p.targetY;
           }
        });
     } else if (mode === 'grid') {
        const count = photos.length;
        const metrics = this.getGridMetrics(count);
        const cols = metrics.cols;
        const rows = metrics.rows;
        
        const cellW = area.width / cols;
        const gridTopInset = Math.min(2.2, Math.max(0.8, (area.height / rows) * 0.08));
        const gridBottomInset = Math.min(1.0, Math.max(0.4, (area.height / rows) * 0.03));
        const usableGridHeight = Math.max(1, area.height - gridTopInset - gridBottomInset);
        const cellH = usableGridHeight / rows;
        
        photos.forEach((p, i) => {
           const r = Math.floor(i / cols);
           const c = i % cols;
           
           p.baseRotation = (Math.random() - 0.5) * 4;
           p.targetRotation = p.baseRotation;
           
           const cellWpx = (cellW / 100) * W;
           const cellHpx = (cellH / 100) * H;
           
           const pWpx = basePhotoW;
           const pHpx = basePhotoW * 1.5;
           
           const angle = Math.abs(p.targetRotation * Math.PI / 180);
           const bbW = pWpx * Math.cos(angle) + pHpx * Math.sin(angle);
           const bbH = pWpx * Math.sin(angle) + pHpx * Math.cos(angle);
           
           const maxScaleW = cellWpx / bbW;
           const maxScaleH = cellHpx / bbH;
           const optimalScale = Math.min(maxScaleW, maxScaleH) * 1.02;
           
           p.targetScale = Math.min(3.0, Math.max(0.1, optimalScale));
           p.originalScale = p.targetScale;
           p.targetX = area.left + (c * cellW) + (cellW / 2);
           p.targetY = area.top + gridTopInset + (r * cellH) + (cellH / 2);
        });
     } else if (mode === 'floating') {
        const count = photos.length;
        const areaWpx = areaMetrics.areaWpx;
        const areaHpx = areaMetrics.areaHpx;
        const slots = this.createCollageSlots(count, area, areaWpx, areaHpx);

        photos.forEach((p, i) => {
           const slot = slots[i % slots.length];
           p.baseRotation = slot.rotation;
           p.targetRotation = slot.rotation;
           const slotWpx = (slot.w / 100) * W;
           const slotHpx = (slot.h / 100) * H;
           const pWpx = basePhotoW;
           const pHpx = basePhotoW * 1.5;
           const angle = Math.abs(p.targetRotation * Math.PI / 180);
           const bbW = pWpx * Math.cos(angle) + pHpx * Math.sin(angle);
           const bbH = pWpx * Math.sin(angle) + pHpx * Math.cos(angle);
           const maxScaleW = slotWpx / bbW;
           const maxScaleH = slotHpx / bbH;
           const optimalScale = Math.min(maxScaleW, maxScaleH) * slot.fill;
           p.targetScale = Math.min(3.2, Math.max(0.18, optimalScale));
           p.originalScale = p.targetScale;
           p.targetX = slot.x + slot.w / 2;
           p.targetY = slot.y + slot.h / 2;
        });
     } else {
        const count = photos.length;
        const areaWpx = areaMetrics.areaWpx;
        const areaHpx = areaMetrics.areaHpx;
        const areaRatio = areaWpx / areaHpx;
        const cols = Math.max(2, Math.ceil(Math.sqrt(count * areaRatio)));
        const rows = Math.max(2, Math.ceil(count / cols));
        const cellW = area.width / cols;
        const cellH = area.height / rows;

        photos.forEach((p, i) => {
           const r = Math.floor(i / cols);
           const c = i % cols;
           const jitterX = ((i * 37) % 100 / 100 - 0.5) * cellW * 0.12;
           const jitterY = ((i * 53) % 100 / 100 - 0.5) * cellH * 0.10;
           const x = area.left + (c * cellW) + (cellW / 2) + jitterX;
           const y = area.top + (r * cellH) + (cellH / 2) + jitterY;

           p.baseRotation = (((i * 29) % 100) / 100 - 0.5) * 8;
           p.targetRotation = p.baseRotation;
           const cellWpx = (cellW / 100) * W;
           const cellHpx = (cellH / 100) * H;
           const pWpx = basePhotoW;
           const pHpx = basePhotoW * 1.5;
           const angle = Math.abs(p.targetRotation * Math.PI / 180);
           const bbW = pWpx * Math.cos(angle) + pHpx * Math.sin(angle);
           const bbH = pWpx * Math.sin(angle) + pHpx * Math.cos(angle);
           const maxScaleW = cellWpx / bbW;
           const maxScaleH = cellHpx / bbH;
           const optimalScale = Math.min(maxScaleW, maxScaleH) * 0.98;
           const emphasis = i % 9 === 0 ? 1.04 : 1;
           p.targetScale = Math.min(3.0, Math.max(0.16, optimalScale * emphasis));
           p.originalScale = p.targetScale;
           p.targetX = Math.min(area.left + area.width - cellW * 0.28, Math.max(area.left + cellW * 0.28, x));
           p.targetY = Math.min(area.top + area.height - cellH * 0.28, Math.max(area.top + cellH * 0.28, y));
        });
     }
     
     this.positionedPhotos.set([...photos]);
  }

  private getFlowSpacing(count: number, area: NonNullable<AppEvent['galleryArea']>) {
    const { areaWpx, ratio } = this.getAreaMetrics(area);
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (ratio >= 1.35 && areaWpx >= 900) {
      const minSpacing = isMobile ? 40 : 15;
      return Math.max(minSpacing, area.width / Math.max(1, Math.min(count, 10)));
    }

    const targetVisible = ratio >= 1
      ? 5
      : ratio >= 0.7
        ? 3.25
        : 2.35;
    const minSpacing = isMobile ? 22 : 8;
    return Math.max(minSpacing, area.width / Math.max(1, targetVisible));
  }

  private getFlowCenterY(area: NonNullable<AppEvent['galleryArea']>) {
    return area.top + (area.height / 2);
  }

  private getFlowExitOffset(area: NonNullable<AppEvent['galleryArea']>, count: number) {
    const spacing = this.getFlowSpacing(Math.max(count, 1), area);
    return Math.min(8, Math.max(3.5, spacing * 0.45));
  }

  private createCollageSlots(count: number, area: NonNullable<AppEvent['galleryArea']>, areaWpx: number, areaHpx: number) {
    const metrics = this.getFloatingMetrics();
    const cols = metrics.cols;
    const slotsPerColumn = Array.from({ length: cols }, (_, col) => Math.floor(count / cols) + (col < count % cols ? 1 : 0));
    const boardWidth = area.width * 0.985;
    const boardLeft = area.left + (area.width - boardWidth) / 2;
    const gap = Math.min(1.1, boardWidth / cols * 0.035);
    const columnW = (boardWidth - (gap * (cols - 1))) / cols;
    const slots: { x: number; y: number; w: number; h: number; rotation: number; fill: number }[] = [];

    let photoIndex = 0;
    for (let col = 0; col < cols; col++) {
      const items = slotsPerColumn[col];
      if (!items) continue;
      const columnHeight = area.height * 0.985;
      const columnTop = area.top + (area.height - columnHeight) / 2;
      const weights = Array.from({ length: items }, (_, row) => {
        const mod = (row + col) % 5;
        return mod === 0 ? 1.22 : mod === 2 ? 0.88 : mod === 4 ? 1.08 : 1;
      });
      const totalWeight = weights.reduce((sum, value) => sum + value, 0);
      let cursorY = columnTop;

      for (let row = 0; row < items; row++) {
        const cellH = (columnHeight * weights[row]) / totalWeight;
        const isFeature = weights[row] > 1.1;
        const widthFactor = isFeature ? 1.0 : 0.96;
        const heightFactor = isFeature ? 1.0 : 0.96;
        const xJitter = (((photoIndex * 17) % 100) / 100 - 0.5) * columnW * 0.04;
        const slotW = columnW * widthFactor;
        const slotH = cellH * heightFactor;
        const slotX = boardLeft + (col * (columnW + gap)) + ((columnW - slotW) / 2) + xJitter;
        const slotY = cursorY + ((cellH - slotH) / 2);
        slots.push({
          x: slotX,
          y: slotY,
          w: slotW,
          h: slotH,
          rotation: (((photoIndex * 23) % 100) / 100 - 0.5) * 7,
          fill: isFeature ? 1.16 : 1.08
        });
        cursorY += cellH;
        photoIndex++;
      }
    }

    return slots.length ? slots : [{ x: area.left, y: area.top, w: area.width, h: area.height, rotation: 0, fill: 1 }];
  }

  startPhysicsLoop() {
     this.zone.runOutsideAngular(() => {
      this.lastPhysicsAt = 0;
      const update = (timestamp = 0) => {
        const hasVideoBackground = !!(this.event()?.galleryBackgroundUrl && this.isVideo(this.event()?.galleryBackgroundUrl));
        const frameInterval = hasVideoBackground ? 33 : 20;
        if (timestamp - this.lastPhysicsAt < frameInterval) {
          this.animFrame = requestAnimationFrame(update);
          return;
        }
        this.lastPhysicsAt = timestamp;
        const mode = this.event()?.galleryLayoutMode || 'infinite-wall';
        const area = this.event()?.galleryArea || { top: 15, left: 5, width: 90, height: 80 };
        
        const photos = this.positionedPhotos();
        const elements = this.photoElementsById();
        let flowDataChanged = false;
        for (const p of photos) {
             const isHovered = this.hoveredId() === p.id;
             
             if ((mode === 'carousel' || mode === 'infinite-wall')) {
                // Movement only if not hovered
                if (!isHovered) {
                   const speed = mode === 'infinite-wall' ? 0.12 : 0.08;
                   p.targetX -= speed;
                }
                
                const limitLeft = area.left - this.getFlowExitOffset(area, photos.length);
                if (p.targetX < limitLeft) {
                   if (this.applyQueuedFlowReplacement(p)) {
                      flowDataChanged = true;
                   }
                   const spacing = this.getFlowSpacing(photos.length, area);
                   const currentMaxX = Math.max(...photos.filter(item => item.id !== p.id).map(o => o.targetX));
                   p.targetX = currentMaxX + spacing;
                   p.x = p.targetX;
                   p.targetY = this.getFlowCenterY(area);
                   p.y = p.targetY;
                   // Reset visual properties when wrapping
                   p.scale = p.targetScale * 0.5;
                }

                // Visual effects based on X position (depth/scale) - Always calculate
                if (mode === 'infinite-wall') {
                   const centerX = area.left + area.width / 2;
                   const distFromCenter = Math.abs(p.x - centerX);
                   // More aggressive effect in foreground
                   const centerEffect = Math.pow(Math.max(0, 1 - (distFromCenter / (area.width / 1.6))), 1.8);
                   
                   p.targetScale = p.originalScale * (1 + centerEffect * 0.35);
                   
                   // Only update zIndex if not hovered to avoid fighting with hoverPhoto logic
                   if (!isHovered && !this.isRecentPriorityPhoto(p)) {
                      p.zIndex = 10 + Math.floor(centerEffect * 200);
                   }
                   
                   p.targetRotation = p.baseRotation * (1 - centerEffect * 0.8);
                } else {
                   if (!isHovered && !this.isRecentPriorityPhoto(p)) {
                      p.zIndex = p.zIndex || 10;
                   }
                }
             }

             const activeTargetScale = isHovered ? p.targetScale * 1.3 : p.targetScale;
             const activeTargetRotation = isHovered ? 0 : p.targetRotation;
             
             // Ease towards target
             const easeX = mode === 'carousel' ? 0.1 : 0.08;
             p.x += (p.targetX - p.x) * easeX;
             p.y += (p.targetY - p.y) * 0.08;
             p.rotation += (activeTargetRotation - p.rotation) * 0.12;
             p.scale += (activeTargetScale - p.scale) * 0.12;
             this.applyPhotoStyle(p, elements.get(p.id), isHovered);
          }
        if (flowDataChanged) {
          this.zone.run(() => this.positionedPhotos.set([...photos]));
        }
        this.animFrame = requestAnimationFrame(update);
      };
      this.animFrame = requestAnimationFrame(update);
     });
  }

  private photoElementsById() {
    const map = new Map<string, HTMLElement>();
    const root = this.container?.nativeElement as HTMLElement | undefined;
    if (!root) return map;
    root.querySelectorAll('.photo-element').forEach((element) => {
      const photoElement = element as HTMLElement;
      const id = photoElement.dataset['id'];
      if (id) map.set(id, photoElement);
    });
    return map;
  }

  private applyPhotoStyle(photo: PositionedPhoto, element: HTMLElement | undefined, isHovered: boolean) {
    if (!element) return;
    if (this.isRecentPriorityPhoto(photo)) {
      photo.zIndex = Math.max(photo.zIndex, 1000000);
    }
    element.style.left = `${photo.x}%`;
    element.style.top = `${photo.y}%`;
    element.style.zIndex = `${photo.zIndex}`;
    element.style.transform = `translate3d(-50%, -50%, 0) perspective(2000px) rotate(${photo.rotation}deg) rotateX(${(photo.y - 50) * 0.2}deg) rotateY(${(photo.x - 50) * -0.2}deg) scale(${photo.scale})`;
    element.style.boxShadow = isHovered ? '0 40px 80px -15px rgba(0,0,0,0.6)' : '0 20px 40px -12px rgba(0,0,0,0.4)';
  }

  private startPhotoRotation() {
    if (this.rotationTimer) clearInterval(this.rotationTimer);
    this.rotationTimer = setInterval(() => void this.rotateVisiblePhoto(), 12000);
  }

  private async rotateVisiblePhoto() {
    let allPhotos = this.photos();
    const mode = this.event()?.galleryLayoutMode || 'infinite-wall';
    const limit = this.getVisiblePhotoLimit();
    if (this.latestPhoto()) return;
    if (this.galleryHasMorePhotos && allPhotos.length <= limit) {
      const loadedPhotos = await this.loadGalleryPhotoBatch();
      if (loadedPhotos.length) {
        allPhotos = this.photos();
      } else {
        await this.syncLatestGalleryPhotos();
        allPhotos = this.photos();
      }
    }
    if (allPhotos.length <= limit) {
      this.trimPreloadedImages();
      return;
    }
    if (this.galleryHasMorePhotos && allPhotos.length - this.rotationCursor < 25) {
      void this.loadGalleryPhotoBatch();
    }

    if (mode === 'grid' || mode === 'floating') {
      this.rotatePhotoPage(allPhotos, limit);
      this.trimPreloadedImages();
      return;
    }

    if (mode === 'carousel' || mode === 'infinite-wall') {
      this.rotateFlowPhotoBatch(allPhotos, limit);
      this.trimPreloadedImages();
      return;
    }

    const visible = this.positionedPhotos();
    const visibleIds = new Set(visible.map(photo => photo.id));
    let nextPhoto: PhotoEntry | undefined;

    for (let attempts = 0; attempts < allPhotos.length; attempts++) {
      this.rotationCursor = (this.rotationCursor + 1) % allPhotos.length;
      const candidate = allPhotos[this.rotationCursor];
      if (candidate && !visibleIds.has(candidate.id)) {
        nextPhoto = candidate;
        break;
      }
    }

    if (!nextPhoto) return;
    this.preloadImage(nextPhoto);
    const rotated = this.createPositionedPhoto(nextPhoto);

    this.positionedPhotos.update(list => {
      const indexFromStart = this.getPhotoRotationRemovalIndex(list, allPhotos);
      const next = list.filter((_, index) => index !== indexFromStart);
      return [rotated, ...next].slice(0, limit);
    });

    this.recalculateLayout();
    this.trimPreloadedImages();
  }

  private rotateFlowPhotoBatch(allPhotos: PhotoEntry[], limit: number) {
    const visible = this.positionedPhotos();
    if (visible.length === 0) return;

    const targetBatchSize = Math.min(this.flowRotationBatchSize, Math.max(1, Math.floor(limit * 0.25)));
    const visibleIds = new Set([
      ...visible.map(photo => photo.id),
      ...this.flowReplacementQueue.map(photo => photo.id)
    ]);
    const nextPhotos = this.getNextNonVisiblePhotos(allPhotos, visibleIds, targetBatchSize);
    if (nextPhotos.length === 0) return;

    nextPhotos.forEach(photo => this.preloadImage(photo));
    this.flowReplacementQueue = [...this.flowReplacementQueue, ...nextPhotos].slice(-(this.flowRotationBatchSize * 3));
  }

  private getNextNonVisiblePhotos(allPhotos: PhotoEntry[], visibleIds: Set<string>, count: number) {
    const selected: PhotoEntry[] = [];
    const blockedIds = new Set(visibleIds);

    for (let attempts = 0; attempts < allPhotos.length && selected.length < count; attempts++) {
      this.rotationCursor = (this.rotationCursor + 1) % allPhotos.length;
      const candidate = allPhotos[this.rotationCursor];
      if (!candidate || blockedIds.has(candidate.id)) continue;
      selected.push(candidate);
      blockedIds.add(candidate.id);
    }

    return selected;
  }

  private applyQueuedFlowReplacement(target: PositionedPhoto) {
    const replacement = this.flowReplacementQueue.shift();
    if (!replacement) return false;

    const currentX = target.x;
    const currentY = target.y;
    const currentTargetX = target.targetX;
    const currentTargetY = target.targetY;
    const currentScale = target.scale;
    const currentTargetScale = target.targetScale;
    const currentOriginalScale = target.originalScale;

    Object.assign(target, replacement, {
      x: currentX,
      y: currentY,
      targetX: currentTargetX,
      targetY: currentTargetY,
      baseRotation: (Math.random() - 0.5) * 8,
      targetRotation: (Math.random() - 0.5) * 8,
      rotation: (Math.random() - 0.5) * 8,
      scale: currentScale,
      targetScale: currentTargetScale,
      originalScale: currentOriginalScale,
      zIndex: target.zIndex
    });

    return true;
  }

  private rotatePhotoPage(allPhotos: PhotoEntry[], limit: number) {
    this.rotationCursor = (this.rotationCursor + limit) % allPhotos.length;
    const page = this.getPhotoPage(allPhotos, this.rotationCursor, limit);
    page.forEach(photo => this.preloadImage(photo));
    const existingById = new Map(this.positionedPhotos().map(photo => [photo.id, photo]));
    this.positionedPhotos.set(page.map(photo => existingById.get(photo.id) || this.createPositionedPhoto(photo)));
    this.recalculateLayout();
  }

  private getPhotoPage(allPhotos: PhotoEntry[], start: number, limit: number) {
    if (allPhotos.length <= limit) return allPhotos;
    const page: PhotoEntry[] = [];
    for (let i = 0; i < limit; i++) {
      page.push(allPhotos[(start + i) % allPhotos.length]);
    }
    return page;
  }

  private placePhotoAtFlowEnd(photo: PositionedPhoto, current: PositionedPhoto[]) {
    const area = this.event()?.galleryArea || { top: 15, left: 5, width: 90, height: 80 };
    const spacing = this.getFlowSpacing(Math.max(current.length || 1, 1), area);
    const furthestX = current.length ? Math.max(...current.map(item => Math.max(item.targetX, item.x))) : area.left + area.width;
    photo.targetX = furthestX + spacing;
    photo.x = photo.targetX;
    photo.targetY = this.getFlowCenterY(area);
    photo.y = photo.targetY;
    photo.scale = photo.targetScale * 0.55;
  }

  private getLeftmostFlowPhotoIndex(list: PositionedPhoto[]) {
    if (list.length <= 1) return 0;
    let leftmostIndex = 0;
    let leftmostX = Number.POSITIVE_INFINITY;
    list.forEach((photo, index) => {
      const flowX = Math.min(photo.targetX, photo.x);
      if (flowX < leftmostX) {
        leftmostX = flowX;
        leftmostIndex = index;
      }
    });
    return leftmostIndex;
  }

  private getPhotoRotationRemovalIndex(list: PositionedPhoto[], allPhotos: PhotoEntry[]) {
    const protectedRecent = new Set(allPhotos.slice(0, 5).map(photo => photo.id));
    const removeIndex = [...list].reverse().findIndex(photo => !protectedRecent.has(photo.id));
    return removeIndex >= 0 ? list.length - 1 - removeIndex : list.length - 1;
  }

  createPositionedPhoto(photo: PhotoEntry): PositionedPhoto {
     this.highestZIndex++;
     const baseScale = this.getRandomScale();
     const baseRot = (Math.random() - 0.5) * 12;
     
     const p = {
        ...photo,
        x: 50,
        y: 150, // Starts off-screen at bottom
        targetX: 50,
        targetY: 50,
        vx: 0,
        vy: 0,
        rotation: (Math.random() - 0.5) * 180, // Spins in
        baseRotation: baseRot,
        targetRotation: baseRot,
        scale: 2.5, // Pop in large
        targetScale: baseScale,
        originalScale: baseScale,
        zIndex: this.highestZIndex
     };
     return p as PositionedPhoto;
  }

  private prioritizeIncomingPhoto(photo: PositionedPhoto) {
    this.highestZIndex += 1000;
    photo.zIndex = this.highestZIndex;
    this.recentPhotoPriorityIds.set(photo.id, Date.now() + this.latestPhotoDisplayMs + 1200);
  }

  private isRecentPriorityPhoto(photo: PositionedPhoto) {
    const expiresAt = this.recentPhotoPriorityIds.get(photo.id);
    if (!expiresAt) return false;
    if (expiresAt < Date.now()) {
      this.recentPhotoPriorityIds.delete(photo.id);
      return false;
    }
    return true;
  }
  
  getRandomScale() {
     const isLarge = typeof window !== 'undefined' && window.innerWidth > 1200;
     return isLarge ? (Math.random() * 0.3 + 0.7) : (Math.random() * 0.3 + 0.8);
  }
  
  getResponsiveWidth() {
    if (typeof window === 'undefined') return 300;
    const w = window.innerWidth;
    const { areaWpx, ratio } = this.getAreaMetrics();
    const baseWidth = w > 1200 ? 350 : w > 768 ? 250 : 180;
    if (ratio >= 1.35 && areaWpx >= 900) return baseWidth;
    const maxAreaAwareWidth = Math.max(150, Math.min(baseWidth, areaWpx * 0.72));
    return Math.round(maxAreaAwareWidth);
  }

  preloadImage(photo: PhotoEntry) {
     if (!isPlatformBrowser(this.platformId)) return;
     const existing = this.preloadedImages.get(photo.id);
     if (existing) {
        this.preloadedImages.delete(photo.id);
        this.preloadedImages.set(photo.id, existing);
        return;
     }
     if (!this.preloadedImages.has(photo.id)) {
        const img = new Image();
        img.src = photo.imageUrl;
        this.preloadedImages.set(photo.id, img);
        this.trimPreloadedImages();
     }
  }

  private trimPreloadedImages() {
     if (!isPlatformBrowser(this.platformId)) return;
     const keepIds = new Set<string>([
        ...this.positionedPhotos().map(photo => photo.id),
        ...this.flowReplacementQueue.map(photo => photo.id),
        ...this.latestPhotoQueue.map(photo => photo.id)
     ]);
     const currentLatest = this.latestPhoto();
     if (currentLatest) keepIds.add(currentLatest.id);

     for (const id of [...this.preloadedImages.keys()]) {
        if (this.preloadedImages.size <= this.maxPreloadedImages) break;
        if (keepIds.has(id)) continue;
        const img = this.preloadedImages.get(id);
        if (img) img.src = '';
        this.preloadedImages.delete(id);
     }

     while (this.preloadedImages.size > this.maxPreloadedImages) {
        const oldestId = this.preloadedImages.keys().next().value;
        if (!oldestId) break;
        const img = this.preloadedImages.get(oldestId);
        if (img) img.src = '';
        this.preloadedImages.delete(oldestId);
     }
  }

  photoRatio(photo: PhotoEntry | null): string {
     if (!photo) return '3/4';
     const img = this.preloadedImages.get(photo.id);
     if (img && img.naturalHeight > 0) {
        return `${img.naturalWidth} / ${img.naturalHeight}`;
     }
     return '3/4';
  }

  showLatestPopup(photo: PhotoEntry) {
    this.latestPhotoQueue.push(photo);
    this.playNextLatestPhoto();
  }

  private playNextLatestPhoto() {
    if (this.latestPhoto() || this.popupTimeout) return;
    const nextPhoto = this.latestPhotoQueue.shift();
    if (!nextPhoto) return;

    this.latestPhoto.set(nextPhoto);
    this.popupTimeout = setTimeout(() => {
      this.latestPhoto.set(null);
      this.popupTimeout = undefined;
      this.playNextLatestPhoto();
    }, this.latestPhotoDisplayMs);
  }

  ngOnDestroy() {
     if (this.popupTimeout) clearTimeout(this.popupTimeout);
     this.latestPhotoQueue = [];
     this.recentPhotoPriorityIds.clear();
     if (this.rotationTimer) clearInterval(this.rotationTimer);
     if (this.gallerySyncTimer) clearInterval(this.gallerySyncTimer);
     if (this.hideControlsTimeout) clearTimeout(this.hideControlsTimeout);
     if (this.animFrame) cancelAnimationFrame(this.animFrame);
     for (const img of this.preloadedImages.values()) img.src = '';
     this.preloadedImages.clear();
     
     if (isPlatformBrowser(this.platformId) && this.fsHandler) {
       document.removeEventListener('fullscreenchange', this.fsHandler);
       document.removeEventListener('webkitfullscreenchange', this.fsHandler);
       document.removeEventListener('mozfullscreenchange', this.fsHandler);
       document.removeEventListener('MSFullscreenChange', this.fsHandler);
     }
  }
}
